
import { useState, useTransition } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Icon } from '@/components/ui/Icon'
import { PresetPicker } from '@/components/projects/PresetPicker'
import type { PresetCard } from '@/lib/presets/specs'
import { btn, card, cx, iconBtn, input, label, notice, pageIntro, pageTitle } from '@/lib/ui'
import type { ProjectSummaryDTO } from '@/lib/types'
import { errorMessage } from '@/lib/client-api'
import { plural } from '@/lib/format'

function formatUpdated(iso: string) {
  const date = new Date(iso)
  const minutes = Math.round((Date.now() - date.getTime()) / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Two counts on a card header. They sit on the gradient, which already
 *  separates them from the card body — the capsule was separating them twice. */
function Stat({ value, unit }: { value: number; unit: string }) {
  return (
    <span className="text-[11px] font-medium text-foreground/70">{plural(value, unit)}</span>
  )
}

export function ProjectsClient({
  initialProjects,
  basePath,
  apiBase,
}: {
  initialProjects: ProjectSummaryDTO[]
  basePath: string
  apiBase: string
}) {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const [picking, setPicking] = useState(false)
  const [creating, setCreating] = useState(false)
  const [presetBusy, setPresetBusy] = useState<string | null>(null)
  const [presetError, setPresetError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function createProject(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const response = await fetch(`${apiBase}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description }),
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error ?? 'Could not create project')
      router.push(`${basePath}/projects/${payload.id}`)
    } catch (cause) {
      setError(errorMessage(cause, 'Could not create project'))
      setBusy(false)
    }
  }

  function openPicker() {
    setError(null)
    setPresetError(null)
    setPicking(true)
  }

  function startBlank() {
    setPicking(false)
    setCreating(true)
  }

  async function addPreset(preset: PresetCard) {
    setPresetError(null)
    setPresetBusy(preset.key)
    try {
      const response = await fetch(`${apiBase}/projects/presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preset: preset.key }),
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error ?? `Could not create "${preset.name}"`)
      router.push(`${basePath}/projects/${payload.id}`)
    } catch (cause) {
      setPresetError(errorMessage(cause, `Could not create "${preset.name}"`))
      setPresetBusy(null)
    }
  }

  async function deleteProject(id: string, projectName: string) {
    if (!confirm(`Delete "${projectName}"? This removes its canvas permanently.`)) return
    setError(null)
    const response = await fetch(`${apiBase}/projects/${id}`, { method: 'DELETE' })
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      setError(payload.error ?? 'Could not delete project')
      return
    }
    startTransition(() => router.refresh())
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-5 py-8 sm:px-8 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className={pageTitle}>Projects</h1>
          <p className={pageIntro}>
            Each project is one system design — a canvas of equipment and cable runs.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => (creating ? setCreating(false) : openPicker())}
            className={cx(btn.base, creating ? btn.secondary : btn.primary)}
          >
            {creating ? (
              'Cancel'
            ) : (
              <>
                <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
                New project
              </>
            )}
          </button>
        </div>
      </div>

      {creating && (
        <form onSubmit={createProject} className={cx(card, 'mt-6 space-y-4 p-5 sm:p-6')}>
          <div>
            <label className={label} htmlFor="project-name">
              Project name
            </label>
            <input
              id="project-name"
              className={cx(input, 'mt-1.5')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="OB Truck 2 — Rugby Series"
              required
              autoFocus
            />
          </div>
          <div>
            <label className={label} htmlFor="project-description">
              Description <span className="normal-case tracking-normal text-muted/70">(optional)</span>
            </label>
            <input
              id="project-description"
              className={cx(input, 'mt-1.5')}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="6-camera setup, Studio A gallery"
            />
          </div>
          <button type="submit" disabled={busy} className={cx(btn.base, btn.primary)}>
            {busy ? 'Creating…' : 'Create and open canvas'}
            {!busy && <Icon name="arrowRight" className="h-4 w-4" />}
          </button>
        </form>
      )}

      {error && <p role="alert" className={cx('mt-4', notice.error)}>{error}</p>}

      {picking && (
        <PresetPicker
          onBlank={startBlank}
          onPreset={addPreset}
          onClose={() => setPicking(false)}
          busyKey={presetBusy}
          error={presetError}
          apiBase={apiBase}
        />
      )}

      {initialProjects.length === 0 ? (
        <div className={cx(card, 'gradient-hero mt-8 overflow-hidden px-6 py-14 text-center sm:px-10')}>
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-surface/80 text-foreground/70 shadow-[var(--shadow-card)]">
            <Icon name="cable" className="h-6 w-6" />
          </span>
          <h2 className="mt-5 text-lg font-semibold tracking-tight">No projects yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted">
            Start with a blank canvas, or drop in a finished preset — a podcast desk, a single-camera
            stream, a four-camera rig — and see the run sheet and power readout filled in.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button type="button" onClick={openPicker} className={cx(btn.base, btn.primary)}>
              <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
              New project
            </button>
          </div>
        </div>
      ) : (
        <ul className="mt-8 grid gap-4 sm:grid-cols-2">
          {initialProjects.map((project) => (
            <li
              key={project.id}
              className={cx(
                card,
                'group relative overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-float)]',
              )}
            >
              <Link href={`${basePath}/projects/${project.id}`} className="block">
                <div className="gradient-hero flex h-24 items-end justify-between px-5 pb-3.5">
                  <span className="flex flex-wrap gap-x-3 gap-y-1">
                    <Stat value={project.nodeCount} unit="device" />
                    <Stat value={project.edgeCount} unit="cable" />
                  </span>
                  <Icon name="cable" className="h-5 w-5 text-foreground/35" />
                </div>
                <div className="p-5">
                  <h2 className="pr-8 text-base font-semibold tracking-tight">{project.name}</h2>
                  {project.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-muted">{project.description}</p>
                  )}
                  <p className="mt-3 text-xs text-muted">Updated {formatUpdated(project.updatedAt)}</p>
                </div>
              </Link>
              {/* Straight to the two pages people want at a venue. The canvas
                  is a poor place to reach them from on a phone, and for a
                  while it was the only one. */}
              <div className="flex gap-1.5 border-t border-[var(--border)] px-5 py-2.5">
                <Link
                  href={`${basePath}/projects/${project.id}/run-sheet`}
                  className={cx(btn.sm, btn.ghost)}
                  title="Cable list — what plugs into what"
                >
                  <Icon name="sheet" className="h-3.5 w-3.5" />
                  Cable list
                </Link>
                <Link
                  href={`${basePath}/power?from=${project.id}`}
                  className={cx(btn.sm, btn.ghost)}
                  title="Power — what this show draws"
                >
                  <Icon name="plug" className="h-3.5 w-3.5" />
                  Power
                </Link>
              </div>
              <button
                type="button"
                onClick={() => deleteProject(project.id, project.name)}
                disabled={isPending}
                aria-label={`Delete ${project.name}`}
                className={cx(
                  iconBtn,
                  // Always there where there is no hover to reveal it with.
                  'absolute right-3 top-3 bg-surface/80 opacity-0 shadow-sm backdrop-blur hover:bg-danger-soft hover:text-danger focus-visible:opacity-100 group-hover:opacity-100',
                  '[@media(hover:none)]:opacity-100',
                )}
              >
                <Icon name="trash" className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
